import { createFileRoute } from "@tanstack/react-router";
import { Badge } from "@timeghost/ui/shadcn/badge";
import { Separator } from "@timeghost/ui/shadcn/separator";
import { type JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminPeriodToggle from "@/components/admin/admin-period-toggle";
import BoxedPage from "@/components/shell/boxed-page";
import {
	type AdminEntityPeriodCounts,
	type AdminStatsPeriod,
	cachedAgo,
	periodValues,
	weekDelta,
	weekDeltaBadgeLabel,
} from "@/lib/admin/types";
import { useAdminStats } from "@/queries/admin/use-admin-stats.query";

const STAT_ENTITY_KEYS = [
	{ key: "users", labelKey: "admin.stats.users" },
	{ key: "workspaces", labelKey: "admin.stats.workspaces" },
	{ key: "clients", labelKey: "admin.stats.clients" },
	{ key: "projects", labelKey: "admin.stats.projects" },
	{ key: "tasks", labelKey: "admin.stats.tasks" },
] as const;

const STAT_SUBSCRIPTION_KEYS = [
	{ key: "subscriptions", labelKey: "admin.stats.subscriptions" },
	{ key: "seats", labelKey: "admin.stats.seats" },
] as const;

const CountCard = ({
	counts,
	label,
	locale,
	period,
	value,
}: {
	readonly counts?: AdminEntityPeriodCounts;
	readonly label: string;
	readonly locale: string;
	readonly period: AdminStatsPeriod;
	readonly value: string;
}): JSX.Element => {
	const { t } = useTranslation();
	const pair = counts ? periodValues(counts, period) : null;
	const delta = pair ? weekDelta(pair.current, pair.last) : null;
	const deltaLabel = pair ? weekDeltaBadgeLabel(pair.current, pair.last, locale) : null;
	const vsKey = period === "month" ? "admin.stats.vsLastMonth" : "admin.stats.vsLastWeek";
	return (
		<div className="flex flex-col gap-1 rounded-lg border border-border bg-well px-3 py-3">
			<div className="flex items-start justify-between gap-2">
				<p className="text-[13px] text-muted-foreground">{label}</p>
				{delta && deltaLabel ? (
					<Badge
						aria-label={t(vsKey, { delta: deltaLabel })}
						variant={delta.signedCount > 0 ? "default" : delta.signedCount < 0 ? "destructive" : "secondary"}>
						{deltaLabel}
					</Badge>
				) : null}
			</div>
			<p className="font-medium text-2xl tabular-nums">{value}</p>
		</div>
	);
};

const GroupList = ({
	empty,
	items,
	title,
}: {
	readonly empty: string;
	readonly items: Readonly<Record<string, number>>;
	readonly title: string;
}): JSX.Element => {
	const { i18n } = useTranslation();
	const entries = Object.entries(items);
	return (
		<section className="flex min-w-0 flex-col gap-2">
			<h2 className="font-medium text-sm">{title}</h2>
			{entries.length === 0 ? (
				<p className="text-muted-foreground text-sm">{empty}</p>
			) : (
				<ul className="flex flex-col gap-1.5">
					{entries.map(([key, count]) => (
						<li className="flex items-center justify-between gap-3 text-sm" key={key}>
							<span className="truncate text-muted-foreground">{key}</span>
							<span className="tabular-nums">{count.toLocaleString(i18n.language)}</span>
						</li>
					))}
				</ul>
			)}
		</section>
	);
};

const AdminStatsPage = (): JSX.Element => {
	const { t, i18n } = useTranslation();
	const query = useAdminStats();
	const [period, setPeriod] = useState<AdminStatsPeriod>("week");

	if (query.isPending) {
		return (
			<BoxedPage actions={<AdminPeriodToggle onChange={setPeriod} value={period} />} title={t("nav.adminStats")}>
				<p className="text-muted-foreground text-sm">{t("admin.loading")}</p>
			</BoxedPage>
		);
	}
	if (query.isError || !query.data) {
		return (
			<BoxedPage actions={<AdminPeriodToggle onChange={setPeriod} value={period} />} title={t("nav.adminStats")}>
				<p className="text-destructive text-sm">{t("admin.loadFailed")}</p>
			</BoxedPage>
		);
	}

	const stats = query.data;

	return (
		<BoxedPage
			actions={<AdminPeriodToggle onChange={setPeriod} value={period} />}
			description={t("admin.stats.cachedAt", { date: cachedAgo(stats.computedAt, i18n.language) })}
			title={t("nav.adminStats")}>
			<div className="flex flex-col gap-6">
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
					{STAT_ENTITY_KEYS.map((item) => (
						<CountCard
							counts={stats[item.key]}
							key={item.key}
							label={t(item.labelKey)}
							locale={i18n.language}
							period={period}
							value={stats[item.key].total.toLocaleString(i18n.language)}
						/>
					))}
				</div>
				<section className="flex flex-col gap-3">
					<Separator />
					<h2 className="font-medium text-sm">{t("admin.stats.subscriptions")}</h2>
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
						{STAT_SUBSCRIPTION_KEYS.map((item) => (
							<CountCard
								counts={stats[item.key]}
								key={item.key}
								label={t(item.labelKey)}
								locale={i18n.language}
								period={period}
								value={stats[item.key].total.toLocaleString(i18n.language)}
							/>
						))}
					</div>
					<div className="grid gap-6 sm:grid-cols-2">
						<GroupList empty={t("admin.empty")} items={stats.subscriptionsByStatus} title={t("admin.stats.byStatus")} />
						<GroupList
							empty={t("admin.empty")}
							items={stats.subscriptionsByProvider}
							title={t("admin.stats.byProvider")}
						/>
					</div>
				</section>
			</div>
		</BoxedPage>
	);
};

export const Route = createFileRoute("/_app/admin/stats/")({
	component: AdminStatsPage,
});
